import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import SuggestionGradientCard from "@/components/company/shared/suggestion-gradient-card";
import DataTableCellCompanyEmployeeEmail from "@/components/data-table-company-employees/data-table-cell-company-employee-email";
import DataTableCellCompanyEmployeePhone from "@/components/data-table-company-employees/data-table-cell-company-employee-phone";
import EmployeeIntegrationsSync from "@/components/integrations/employee/employee-integrations-sync";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToastError } from "@/components/ui/use-toast";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyDTO,
  EmployeeDTO,
  EmployeeSearchResponse,
} from "@shaarp/common";
import { useEffect, useState } from "react";
import { RiLinkedinBoxFill } from "react-icons/ri";

/**
 * Forces the email/phone DataTable cell components to behave like full-width
 * stacked rows inside the side panel: their reveal button stretches and the
 * follow-up button row truncates instead of overflowing.
 */
const COMPACT_CELL_OVERRIDES =
  "min-w-0 [&>button]:w-full [&>div]:max-w-full " +
  "[&>div>button:first-child]:min-w-0 [&>div>button:first-child]:flex-1 " +
  "[&>div>button:first-child]:overflow-hidden [&>div>button:first-child]:truncate " +
  "[&>div>button:first-child]:justify-start";

function TopExecutiveSkeleton() {
  return (
    <>
      <div className="flex items-center gap-3">
        <Skeleton className="w-11 h-11 rounded-full shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </>
  );
}

function TopExecutiveCard({
  employee,
  companyId,
  onEmployeeChanged,
  showHubspotSync,
}: {
  employee: EmployeeDTO;
  /** Absent for a company the base does not hold: reveals and CRM run without it. */
  companyId?: string;
  onEmployeeChanged: (update: Partial<EmployeeDTO> & { id: string }) => void;
  showHubspotSync: boolean;
}) {
  const fullName = [employee.firstName, employee.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const initials = `${employee.firstName?.charAt(0) ?? ""}${
    employee.lastName?.charAt(0) ?? ""
  }`.toUpperCase();

  const { teamSelected } = useTeams();
  const showEmail = teamSelected?.featureContactRevealEmailEnabled ?? true;
  const showPhone = teamSelected?.featureContactRevealPhoneEnabled ?? true;

  return (
    <>
      <div className="flex items-center gap-3">
        <Avatar className="w-11 h-11 shrink-0">
          <AvatarImage src={employee.photoUrl ?? ""} alt={fullName} />
          <AvatarFallback className="text-sm font-semibold">
            {initials || "?"}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-semibold truncate">
            {fullName || "—"}
          </span>
          {employee.jobTitle && (
            <span className="text-xs text-muted-foreground truncate">
              {employee.jobTitle}
            </span>
          )}
        </div>
        {employee.linkedInUrl && (
          <Button size="icon" variant="ghost" className="shrink-0" asChild>
            <a
              href={employee.linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RiLinkedinBoxFill className="w-5 h-5 text-[#0a66c2]" />
            </a>
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {showEmail && (
          <div className={COMPACT_CELL_OVERRIDES}>
            <DataTableCellCompanyEmployeeEmail
              employee={employee}
              companyId={companyId}
              onEmailRevealed={onEmployeeChanged}
              compact
            />
          </div>
        )}
        {showPhone && (
          <div className={COMPACT_CELL_OVERRIDES}>
            <DataTableCellCompanyEmployeePhone
              employee={employee}
              companyId={companyId}
              onPhoneRevealed={onEmployeeChanged}
              compact
            />
          </div>
        )}
        {showHubspotSync && (
          <EmployeeIntegrationsSync
            employee={employee}
            companyId={companyId}
            onSynced={onEmployeeChanged}
            showLabel
          />
        )}
      </div>
    </>
  );
}

/**
 * Whose people are searched: a company of the base, or the website of one the
 * base does not hold (a chat tag's web profile), searched without creating it.
 */
type TopExecutiveTarget = { company: CompanyDTO } | { domain: string };

/** The company's top executive; nothing, not even a fetch, for a team whose direct contact is cut (#757). */
export default function TopExecutiveSection(target: TopExecutiveTarget) {
  const arePeopleVisible = useArePeopleVisible();
  if (!arePeopleVisible) return null;
  return <TopExecutiveSectionContent {...employeesSearchOf(target)} />;
}

/** The company the reveals attach to, if any, and the route listing its people. */
function employeesSearchOf(target: TopExecutiveTarget): {
  companyId?: string;
  employeesPath: string;
} {
  if ("company" in target) {
    return {
      companyId: target.company.id,
      employeesPath: `/company/${target.company.id}/employees`,
    };
  }
  return {
    employeesPath: `/company/domain/${encodeURIComponent(target.domain)}/employees`,
  };
}

function TopExecutiveSectionContent({
  companyId,
  employeesPath,
}: {
  companyId?: string;
  employeesPath: string;
}) {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { teamUser } = useUser();
  const { isMobile } = useDeviceFormat();

  const [employee, setEmployee] = useState<EmployeeDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const query =
    teamUser?.customSearchContactsQuery ||
    t("components.dataTableCompanies.columns.sidePanelExecutiveFallbackQuery");

  useEffect(() => {
    let isCancelled = false;
    const fetchExecutive = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<EmployeeSearchResponse>(
          `${employeesPath}?query=${encodeURIComponent(query)}&page=1&limit=1`,
        );
        if (!isCancelled) {
          setEmployee(data.employees[0] ?? null);
        }
      } catch {
        if (!isCancelled) {
          setEmployee(null);
          toastError();
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchExecutive();
    return () => {
      isCancelled = true;
    };
    // useApi/useToastError return fresh references on every render, so we
    // intentionally key the effect on the company's path (and the team's
    // custom contact query) only — to avoid an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesPath, query]);

  const handleEmployeeChanged = (
    update: Partial<EmployeeDTO> & { id: string },
  ) => {
    setEmployee((prev) =>
      prev && prev.id === update.id ? { ...prev, ...update } : prev,
    );
  };

  if (!isLoading && !employee) return null;

  return (
    <section>
      <SectionHeaderWithIcon
        title={t("components.dataTableCompanies.columns.sidePanelExecutive")}
        className="mb-3"
      />
      <SuggestionGradientCard
        badgeLabel={t(
          "components.dataTableCompanies.columns.sidePanelExecutiveSuggestion",
        )}
      >
        {isLoading ? (
          <TopExecutiveSkeleton />
        ) : (
          <TopExecutiveCard
            employee={employee!}
            companyId={companyId}
            onEmployeeChanged={handleEmployeeChanged}
            showHubspotSync={connectedCrmIntegrations.length > 0 && !isMobile}
          />
        )}
      </SuggestionGradientCard>
    </section>
  );
}
